/**
 * Find the glossary terms an Ask answer actually used.
 *
 * The panel lists these under TERMS, beside SOURCES: sources say where a claim came from,
 * terms say what the words in it mean. Both are provenance, for different halves of an
 * answer.
 *
 * DETERMINISTIC, AND DELIBERATELY SO. The obvious alternative is to let the model tag its
 * own terms, which costs a round trip and lets it invent one — a "glossary term" that is
 * not in the glossary is worse than no TERMS section at all, because the whole point of
 * §44 is that a term has exactly one definition and this list is a promise you can click.
 * Matching in code cannot produce a term the corpus does not contain.
 *
 * WHICH CORPUS IS A SAFETY QUESTION, NOT A DETAIL. The corpus is a parameter, never
 * imported here: `audience: internal` entries must never surface to a brand reader
 * (§43/§44 — `tag-family` is the live example). Callers pass `useGlossaryCorpus()`, which
 * fails closed to the brand corpus for anything that cannot prove it is staff. This module
 * has no opinion and no default, so it cannot be the thing that leaks.
 */
import type {GlossaryEntry} from 'docs/glossary'

export interface GlossaryTermMatch {
  entry: GlossaryEntry
  /** Offsets into the source text, for highlighting. Non-overlapping, ascending. */
  ranges: {start: number; end: number}[]
}

/** Characters that may sit either side of a match. Anything else means it is mid-word. */
const isBoundary = (ch: string | undefined): boolean => ch === undefined || !/[\p{L}\p{N}]/u.test(ch)

/**
 * Every surface form that should resolve to an entry: its term, its aliases, and its id
 * with hyphens read as spaces (ids are kebab-case, so `tag-family` also matches the
 * phrase "tag family" as written in prose).
 */
const surfaceFormsOf = (entry: GlossaryEntry): string[] => {
  const forms = [entry.term, ...(entry.aliases ?? []), entry.id.replace(/-/g, ' ')]
  return forms.map(f => f.trim()).filter(f => f.length > 1)
}

/**
 * Match glossary terms in `text`.
 *
 * Longest form first, so "digital certificate" wins over "certificate" and the shorter
 * term does not shadow the more specific one. Claimed ranges are never re-matched, which
 * is what makes that ordering stick.
 *
 * Word-boundary matched on Unicode letters/numbers rather than `\b`, so "certificated"
 * does not match "certificate" and an accented term still matches.
 */
export const findGlossaryTerms = (
  text: string,
  corpus: readonly GlossaryEntry[],
): GlossaryTermMatch[] => {
  if (!text.trim() || corpus.length === 0) return []

  const haystack = text.toLowerCase()
  const claimed: boolean[] = new Array(text.length).fill(false)
  const byId = new Map<string, GlossaryTermMatch>()

  const candidates = corpus
    .flatMap(entry => surfaceFormsOf(entry).map(form => ({entry, form: form.toLowerCase()})))
    .sort((a, b) => b.form.length - a.form.length)

  for (const {entry, form} of candidates) {
    let from = 0
    for (;;) {
      const at = haystack.indexOf(form, from)
      if (at === -1) break
      const end = at + form.length
      from = at + 1

      if (!isBoundary(text[at - 1]) || !isBoundary(text[end])) continue
      let overlaps = false
      for (let i = at; i < end; i++) {
        if (claimed[i]) {
          overlaps = true
          break
        }
      }
      if (overlaps) continue

      for (let i = at; i < end; i++) claimed[i] = true
      const existing = byId.get(entry.id)
      if (existing) existing.ranges.push({start: at, end})
      else byId.set(entry.id, {entry, ranges: [{start: at, end}]})
    }
  }

  return [...byId.values()]
    .map(m => ({...m, ranges: m.ranges.sort((a, b) => a.start - b.start)}))
    .sort((a, b) => a.ranges[0].start - b.ranges[0].start)
}

/**
 * Split `text` into rendered segments, so a caller can highlight without doing its own
 * offset arithmetic (and without `dangerouslySetInnerHTML`, which is how a highlighter
 * usually turns into an injection bug).
 *
 * Only the FIRST occurrence of each term is marked. Highlighting all of them turns a
 * paragraph that says "certificate" six times into a wall of underlines, which reads as
 * noise and buries the one term the reader did not know.
 */
export interface AnswerSegment {
  text: string
  entry?: GlossaryEntry
}

export const segmentAnswer = (
  text: string,
  corpus: readonly GlossaryEntry[],
): AnswerSegment[] => {
  const matches = findGlossaryTerms(text, corpus)
  if (matches.length === 0) return [{text}]

  const firsts = matches
    .map(m => ({entry: m.entry, ...m.ranges[0]}))
    .sort((a, b) => a.start - b.start)

  const out: AnswerSegment[] = []
  let cursor = 0
  for (const {entry, start, end} of firsts) {
    if (start > cursor) out.push({text: text.slice(cursor, start)})
    out.push({text: text.slice(start, end), entry})
    cursor = end
  }
  if (cursor < text.length) out.push({text: text.slice(cursor)})
  return out
}
